import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForumTab = ({ currentUser, getAuthConfig }) => {
  const [posts, setPosts] = useState([]);
  const [showNewPost, setShowNewPost] = useState(false);
  const [newPost, setNewPost] = useState({ title: '', content: '', category: 'general' });
  const [expandedPost, setExpandedPost] = useState(null);
  const [replyText, setReplyText] = useState('');

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API}/forum`, getAuthConfig());
      setPosts(res.data);
    } catch (e) { console.error('Forum fetch error:', e); }
  };
  useEffect(() => { fetchPosts(); }, []);

  const submitPost = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/forum`, newPost, getAuthConfig());
      setNewPost({ title: '', content: '', category: 'general' });
      setShowNewPost(false);
      fetchPosts();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to post'); }
  };

  const submitReply = async (postId) => {
    if (!replyText.trim()) return;
    try {
      await axios.post(`${API}/forum/${postId}/reply`, { content: replyText }, getAuthConfig());
      setReplyText('');
      fetchPosts();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to reply'); }
  };

  const deletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API}/forum/${postId}`, getAuthConfig());
      if (expandedPost === postId) setExpandedPost(null);
      fetchPosts();
    } catch (err) { alert(err.response?.data?.detail || 'Failed to delete'); }
  };

  const categoryColors = { general: '#6b7280', question: '#2563eb', tip: '#059669', housing: '#d97706', social: '#8b5cf6' };

  return (
    <div data-testid="forum-section">
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2>Community Forum</h2>
          <p>Ask questions, share tips, and connect with other members</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowNewPost(!showNewPost)} style={{ padding: '10px 18px', whiteSpace: 'nowrap' }} data-testid="new-post-btn">
          {showNewPost ? 'Cancel' : '+ New Post'}
        </button>
      </div>

      {showNewPost && (
        <form onSubmit={submitPost} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', padding: '20px', marginBottom: '20px' }} data-testid="new-post-form">
          <div style={{ marginBottom: '12px' }}>
            <input type="text" placeholder="Post title..." value={newPost.title} onChange={e => setNewPost({ ...newPost, title: e.target.value })} required
              style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '15px', fontWeight: '600', boxSizing: 'border-box' }} data-testid="post-title-input" />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <select value={newPost.category} onChange={e => setNewPost({ ...newPost, category: e.target.value })}
              style={{ padding: '8px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px' }} data-testid="post-category-select">
              <option value="general">General</option>
              <option value="question">Question</option>
              <option value="tip">Tip / Advice</option>
              <option value="housing">Housing</option>
              <option value="social">Social</option>
            </select>
          </div>
          <div style={{ marginBottom: '14px' }}>
            <textarea placeholder="Write your post..." value={newPost.content} onChange={e => setNewPost({ ...newPost, content: e.target.value })} required rows={4}
              style={{ width: '100%', padding: '10px 14px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box' }} data-testid="post-content-input" />
          </div>
          <button type="submit" className="btn btn-primary" style={{ padding: '10px 24px' }} data-testid="submit-post-btn">Post</button>
        </form>
      )}

      {posts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280', background: 'white', borderRadius: '10px', border: '1px solid #e5e7eb' }}>No posts yet. Start the conversation!</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {posts.map(post => (
            <div key={post.post_id} style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '10px', overflow: 'hidden' }} data-testid={`forum-post-${post.post_id}`}>
              <div style={{ padding: '16px', cursor: 'pointer' }} onClick={() => setExpandedPost(expandedPost === post.post_id ? null : post.post_id)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                  <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '10px', background: `${categoryColors[post.category] || '#6b7280'}18`, color: categoryColors[post.category] || '#6b7280', textTransform: 'uppercase' }}>{post.category}</span>
                  <strong style={{ fontSize: '15px', color: '#1a2332' }}>{post.title}</strong>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '12px', color: '#6b7280' }}>
                  <span>{post.author_name}</span>
                  <span>{new Date(post.created_at).toLocaleDateString()}</span>
                  <span style={{ color: '#2563eb', fontWeight: '600' }}>{post.replies?.length || 0} {post.replies?.length === 1 ? 'reply' : 'replies'}</span>
                </div>
              </div>

              {expandedPost === post.post_id && (
                <div style={{ borderTop: '1px solid #f3f4f6', padding: '16px' }}>
                  <p style={{ fontSize: '14px', color: '#374151', lineHeight: '1.6', margin: '0 0 16px', whiteSpace: 'pre-wrap' }}>{post.content}</p>
                  {post.author_id === currentUser?.user_id && (
                    <button onClick={() => deletePost(post.post_id)} style={{ fontSize: '12px', color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', marginBottom: '12px', fontWeight: '600' }} data-testid={`delete-post-${post.post_id}`}>Delete Post</button>
                  )}
                  {post.replies?.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                      {post.replies.map(r => (
                        <div key={r.reply_id} style={{ background: '#f9fafb', padding: '10px 14px', borderRadius: '8px', borderLeft: '3px solid #e5e7eb' }}>
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                            <strong style={{ color: '#1a2332' }}>{r.author_name}</strong> &middot; {new Date(r.created_at).toLocaleDateString()}
                          </div>
                          <p style={{ fontSize: '13px', color: '#374151', margin: 0, whiteSpace: 'pre-wrap' }}>{r.content}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <input type="text" placeholder="Write a reply..." value={replyText} onChange={e => setReplyText(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') submitReply(post.post_id); }}
                      style={{ flex: 1, padding: '8px 12px', border: '2px solid #e5e7eb', borderRadius: '8px', fontSize: '13px' }} data-testid={`reply-input-${post.post_id}`} />
                    <button className="btn btn-primary" style={{ padding: '8px 16px', fontSize: '13px' }} onClick={() => submitReply(post.post_id)} data-testid={`reply-btn-${post.post_id}`}>Reply</button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ForumTab;
